import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useSearchDestinations,
  useUpdatePartnerSettings,
} from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import type {
  PartnerDestination,
  PartnerSettings,
} from "@/types/partner-settings";
import { cn } from "@/lib/utils";

export type GuesteriaPartnerEditRegionDialogProps = {
  trigger: React.ReactNode;
  settings: PartnerSettings;
};

function destinationFromSettings(
  settings: PartnerSettings,
): PartnerDestination | null {
  const id = settings.destinationId?.trim();
  const name = settings.destinationName?.trim();
  if (!id || !name) {
    return null;
  }
  return {
    destinationId: id,
    name,
    type: settings.destinationType?.trim() || "CITY",
  };
}

export function GuesteriaPartnerEditRegionDialog({
  trigger,
  settings,
}: GuesteriaPartnerEditRegionDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<PartnerDestination | null>(null);

  const updatePartnerSettings = useUpdatePartnerSettings();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: destinations, isLoading: isSearching } =
    useSearchDestinations(debouncedQuery);

  const syncFromSettings = React.useCallback(() => {
    setSelected(destinationFromSettings(settings));
    const name = settings.destinationName ?? "";
    setInputValue(name);
    setDebouncedQuery(name);
  }, [settings]);

  useEffect(() => {
    if (open) {
      syncFromSettings();
    }
  }, [open, syncFromSettings]);

  const canSave = selected != null;
  const isSaving = updatePartnerSettings.isPending;

  const trimmedDebouncedQuery = debouncedQuery.trim();
  let destinationListBody: React.ReactNode;
  if (trimmedDebouncedQuery.length === 0) {
    destinationListBody = (
      <div className="text-muted-foreground px-3 py-2 text-center text-sm">
        {t("partners.detail.editRegion.typeToSearch")}
      </div>
    );
  } else if (isSearching) {
    destinationListBody = (
      <div className="text-muted-foreground px-3 py-2 text-center text-sm">
        {t("partners.searching")}
      </div>
    );
  } else if (!destinations?.length) {
    destinationListBody = (
      <div className="text-muted-foreground px-3 py-2 text-center text-sm">
        {t("partners.noDestinationsFound")}
      </div>
    );
  } else {
    destinationListBody = destinations.map((dest) => {
      const isActive = selected?.destinationId === dest.destinationId;
      return (
        <button
          key={dest.destinationId}
          type="button"
          className={cn(
            "flex w-full flex-col gap-0.5 rounded-sm px-3 py-2 text-left text-sm transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            isActive && "bg-accent text-accent-foreground",
          )}
          onClick={() => {
            setSelected(dest);
            setInputValue(dest.name);
          }}
        >
          <span className="font-medium">{dest.name}</span>
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            <MapPin className="size-3 shrink-0" aria-hidden />
            {dest.type} · {dest.destinationId}
          </span>
        </button>
      );
    });
  }

  const handleSave = () => {
    if (!selected) {
      return;
    }
    updatePartnerSettings.mutate(
      {
        enabled: settings.enabled,
        destinationId: selected.destinationId,
        destinationName: selected.name,
        destinationType: selected.type,
      },
      {
        onSuccess: () => {
          toast.success(t("partners.saveSuccess"));
          setOpen(false);
        },
        onError: () => {
          toast.error(t("partners.saveError"));
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <span className="inline-flex shrink-0">{trigger}</span>
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className="flex max-h-[min(90vh,640px)] w-[calc(100%-2rem)] max-w-[360px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-0 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)] sm:max-w-[360px]"
      >
        <div className="relative flex shrink-0 flex-col border-b border-[#E5E5E5] px-4 pt-4 pb-3">
          <DialogTitle className="text-center text-base leading-normal font-semibold text-[#171717]">
            {t("partners.detail.editRegion.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("partners.detail.editRegion.formDescription")}
          </DialogDescription>
          <button
            type="button"
            className="absolute top-3 right-3 rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
            onClick={() => setOpen(false)}
            aria-label={t("partners.detail.editRegion.closeAria")}
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold text-[#171717]">
              {t("partners.detail.editRegion.destinationLabel")}
            </Label>
            <div className="relative">
              <Input
                placeholder={t("partners.searchDestinationPlaceholder")}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoComplete="off"
                className="h-9 border-[#E5E5E5] pr-10 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
              />
              <span
                className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[#737373]"
                aria-hidden
              >
                <Search className="size-4" />
              </span>
            </div>

            <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
              <div className="max-h-[200px] overflow-y-auto p-1">
                {destinationListBody}
              </div>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[#E5E5E5] px-4 py-3">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => setOpen(false)}
          >
            {t("partners.detail.editRegion.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!canSave || isSaving}
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597] disabled:opacity-50"
            onClick={handleSave}
          >
            {isSaving
              ? t("partners.saving")
              : t("partners.detail.editRegion.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
